import { produce } from 'immer';
import { entity } from 'simpler-state';

import entityReduxLogger from '../debug/helpers/entity-redux-logger';
import { BBODataCache, InstrumentSnapshotMap } from '../store/market-data/types';
import { NullableNumber } from '../util/types';


type CalculatedNBBOPrice = {
  symbol: string
  askprice: NullableNumber
  bidprice: NullableNumber
}
type CalculatedNBBOPriceData = Partial<Record<string, CalculatedNBBOPrice>>

const NBBOPricesCache = entity({} as CalculatedNBBOPriceData, entityReduxLogger('NBBO', 'nbbo'));

export const generateEmptyNBBOPrice = (symbol: string) => ({
  symbol,
  askprice: null,
  bidprice: null,
} as CalculatedNBBOPrice);

type DataSourceType = 'instr' | 'nbbo';

function readPrices(
  newCache: BBODataCache | InstrumentSnapshotMap,
  symbol: string,
  sourceType: DataSourceType,
) {
  if (sourceType === 'instr') {
    const { askPrice, bidPrice } = (newCache as InstrumentSnapshotMap)[symbol];
    return { askprice: askPrice, bidprice: bidPrice };
  }

  const { askprice, bidprice } = (newCache as BBODataCache)[symbol];
  return { askprice, bidprice };
}

function updateNewState(
  newCache: BBODataCache | InstrumentSnapshotMap,
  state: CalculatedNBBOPriceData,
  sourceType: DataSourceType,
) {
  for (const symbol in newCache) {
    const { askprice, bidprice } = readPrices(newCache, symbol, sourceType);
    const cachedItem = state[symbol];

    // Replacing an item with identical prices would still mark the immer draft dirty and re-render
    // every subscriber, so only write when a price actually changed.
    if (!cachedItem || cachedItem.askprice !== askprice || cachedItem.bidprice !== bidprice) {
      state[symbol] = { symbol, askprice, bidprice };
    }
  }
}

/** Used only by Gateway service to update cache from response messages. */
export const updateNBBOPricesCache = (newItems: BBODataCache | InstrumentSnapshotMap, sourceType: DataSourceType) => {
  NBBOPricesCache.set(
    produce(state => updateNewState(newItems, state, sourceType)),
  );
};


// exporting setter only for unit testing and mocked mode
export const _setNBBOPricesCache_InUnitTestsAndMockedMode = NBBOPricesCache.set; // eslint-disable-line no-underscore-dangle

const symbolTransform = (symbol: string) => (state: CalculatedNBBOPriceData) => state[symbol];

// hiding direct setter by not including it in exports
export default {
  use: (symbol: string) => NBBOPricesCache.use(symbolTransform(symbol)) ?? generateEmptyNBBOPrice(symbol),
  get: (symbol: string) => NBBOPricesCache.get()[symbol] ?? generateEmptyNBBOPrice(symbol),
  getAll: NBBOPricesCache.get,
  useAll: NBBOPricesCache.use,
};
